/**
 * Fluent Ad Tech Configuration
 * This file centralizes all Fluent-related configuration (set in OSGi).
 */

/**
 * Get Fluent configuration from server-side OSGi configuration.
 * Configuration is injected by AEM HTL template via data attribute.
 * @returns {Object} Server-provided configuration
 */
function getServerConfig() {
  // Read from data attribute on config element injected by AEM
  const configElement = document.querySelector('[data-fluent-config]');
  if (configElement) {
    try {
      return JSON.parse(configElement.dataset.fluentConfig || '{}');
    } catch (e) {
      console.error('[Fluent] Failed to parse server configuration:', e);
    }
  } else{
    console.warn('[Fluent] data-fluent-config element NOT found on this page:');
  }
  return null;
}

/**
 * Fluent Configuration Object
 * Values are loaded from server-side OSGi configuration
 */
const FLUENT_CONFIG = getServerConfig();

/**
 * Get current environment from server configuration
 * @returns {string}
 */
export function getEnvironment() {
  return (FLUENT_CONFIG && FLUENT_CONFIG.environment) || 'production';
}

/**
 * Get Fluent Partner ID from OSGi configuration
 * @returns {string}
 */
export function getFluentPartnerId() {
  return (FLUENT_CONFIG && FLUENT_CONFIG.partnerId) || '';
}

/**
 * Get Fluent Placement ID from OSGi configuration
 * @returns {string}
 */
export function getFluentPlacementId() {
  return (FLUENT_CONFIG && FLUENT_CONFIG.placementId) || '';
}

/**
 * Get Fluent SDK URLs from OSGi configuration
 * @returns {{cssUrl: string, jsUrl: string}}
 */
export function getFluentSDKUrls() {
  return {
    cssUrl: (FLUENT_CONFIG && FLUENT_CONFIG.cssUrl) || '',
    jsUrl: (FLUENT_CONFIG && FLUENT_CONFIG.jsUrl) || ''
  };
}


/**
 * Check if Fluent is enabled (from OSGi configuration)
 * @returns {boolean}
 */
export function isFluentEnabled() {
  return FLUENT_CONFIG && FLUENT_CONFIG.enabled === true;
}

/**
 * Get enabled country list from OSGi configuration
 * @returns {string[]}
 */
export function getFluentEnabledCountries() {
  return (FLUENT_CONFIG && FLUENT_CONFIG.countryFilter) || [];
}

/**
 * Log debug message (only if not in production environment)
 * @param {string} message
 * @param {...any} args
 */
export function fluentDebugLog(message, ...args) {
  if (getEnvironment() !== 'production') {
    console.log(`[Fluent] ${message}`, ...args);
  }
}

/**
 * Get full Fluent configuration
 * Useful for debugging or inspection
 * @returns {Object}
 */
export function getFluentConfig() {
  return {
    partnerId: getFluentPartnerId(),
    placementId: getFluentPlacementId(),
    sdkUrls: getFluentSDKUrls(),
    environment: getEnvironment(),
    enabled: isFluentEnabled(),
    countryFilter: getFluentEnabledCountries()
  };
}

// Export the raw config for direct access if needed (from OSGi)
export { FLUENT_CONFIG };

// Default export
export default {
  getFluentPartnerId,
  getFluentPlacementId,
  getFluentSDKUrls,
  isFluentEnabled,
  getFluentEnabledCountries,
  fluentDebugLog,
  getFluentConfig,
  FLUENT_CONFIG
};
